import { ComponentType, useMemo } from 'react';
import Chart from '@components/charts/Chart';
import calculateMidColor from '@utilities/colors';
import merge from 'lodash/merge';
import PieChartProxy from '@components/charts/pieChartProxy';
import { toSignificant } from '@/utilities/formatting';
import type { ComputedPortfolioHolding } from '@/components/DeprecatedPortfolio/redux/interface';

interface Props {
  portfolioName: string;
  currencySymbol: string;
  lastUpdated: number;
  options?: Record<string, unknown>;
  holdings: ComputedPortfolioHolding[];
  portfolioId?: string;
}
function HoldingsReturns(props: Props) {
  const minMax = 0.1;
  const data = useMemo(() => {
    return props.holdings
      .filter((n) => !n.exclude)
      .filter((item) => typeof item.dividendReturn === 'number')
      .map((item) => {
        if (item.dividendReturn)
          return {
            name:
              item.tickerSymbol +
              '<br/>Return:<br/>' +
              toSignificant(item.dividendReturn * 100, 2) +
              '%',
            y: Math.round(Math.abs(item.totalGain || 0) * 100) / 100,
            color: calculateMidColor(
              '#DF5353',
              '#55BF3B',
              Math.min(Math.max(item.dividendReturn, -minMax), minMax) *
                (0.5 / minMax) +
                0.5,
              1
            ),
          };
      });
  }, [props.holdings]);
  const chartOptions = useMemo(() => {
    return merge({}, props.options, {
      tooltip: {
        headerFormat: '{point.key}',
        pointFormat:
          '<br>Gain/Loss value: <b>' + props.currencySymbol + '{point.y}</b>',
      },
      plotOptions: {
        pie: {
          dataLabels: {
            color: '#FFFFFF',
            style: {
              fontFamily: '"Roboto"',
              fontWeight: 'normal',
              fontSize: '1.3rem',
              color: '#FFFFFF',
              textShadow: 'none',
              textOutline: 'none',
            },
          },
        },
      },
      series: [
        {
          type: 'pie',
          name: props.portfolioName,
          data: data,
        },
      ],
    });
  }, [data, props.currencySymbol, props.portfolioName, props.options]);
  return (
    <>
      {props.holdings.length > 0 && (
        <Chart
          options={chartOptions}
          modifier={`${props.portfolioId}-${props.lastUpdated}`}
        />
      )}
    </>
  );
}

HoldingsReturns.defaultProps = {
  portfolioId: '',
  lastUpdated: 0,
};

const HoldingsReturnsChart = PieChartProxy(
  HoldingsReturns
) as ComponentType<Props>;

export { HoldingsReturnsChart };
